import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '18网页游戏',
  url: 'https://www.18webgame.com',
  description: '精选热门网页游戏大全，无需下载即点即玩。覆盖角色扮演、策略、射击、休闲等多种类型。',
  foundingDate: '2018',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@18webgame.com',
    availableLanguage: ['Chinese'],
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.18webgame.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.18webgame.com/about' },
  ],
};

const stats = [
  { value: '1000+', label: '精选游戏' },
  { value: '5000万+', label: '活跃玩家' },
  { value: '8年', label: '稳定运营' },
  { value: '99.9%', label: '服务可用率' },
];

export default function AboutPage() {
  const { pathname } = useLocation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 18网页游戏';
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [pathname]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput?.value.trim()) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9phs1mb4qjhggaepup0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb + Title */}
        <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium whitespace-nowrap">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">关于18网页游戏</h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">致力于为玩家提供最优质的网页游戏体验</p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>18网页游戏</strong>成立于2018年，是国内领先的网页游戏导航与资讯平台。我们的使命是<strong>让每一位玩家都能轻松找到并享受优质网页游戏</strong>，无需繁琐的下载安装过程，即点即玩，随时随地畅享游戏乐趣。
                  </p>
                  <p>
                    经过8年的深耕发展，18网页游戏已经汇聚了<strong>超过1000款</strong>精选网页游戏，覆盖角色扮演、策略塔防、射击竞技、休闲益智、体育竞速、卡牌桌游等<strong>六大热门品类</strong>。累计服务超过<strong>5000万</strong>玩家，日均活跃用户超过<strong>200万</strong>。
                  </p>
                  <p>
                    我们的编辑团队由<strong>资深游戏玩家和专业游戏媒体人</strong>组成，持续为玩家提供最新游戏资讯、深度评测、实用攻略等优质内容。每一款上架游戏都经过严格筛选和测试，确保为玩家带来最佳游戏体验。
                  </p>
                  <p>
                    未来，18网页游戏将继续秉持<strong>「精选、便捷、安全」</strong>的服务理念，不断拓展游戏资源、优化平台体验，努力成为中文互联网最好的网页游戏平台。
                  </p>
                </div>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20gaming%20office%20environment%20with%20multiple%20monitors%20displaying%20colorful%20game%20screens%2C%20team%20of%20young%20creative%20people%20discussing%2C%20warm%20indoor%20lighting%2C%20energetic%20tech%20startup%20atmosphere%2C%20professional%20yet%20playful%20vibe%2C%20editorial%20photography%20style&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="18网页游戏团队"
                    title="18网页游戏 - 专业游戏平台团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">我们的成绩</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 md:p-8 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-heading text-accent-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">为什么选择我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有游戏经过严格安全审核，浏览器沙盒运行，保障设备安全' },
                { icon: 'ri-flashlight-line', title: '即点即玩', desc: '无需下载安装，打开浏览器就能畅玩，方便快捷零门槛' },
                { icon: 'ri-star-line', title: '精选推荐', desc: '编辑团队精心筛选，只推荐高品质好玩的网页游戏' },
                { icon: 'ri-article-line', title: '专业评测', desc: '资深玩家深度评测，帮你快速了解游戏优缺点' },
                { icon: 'ri-smartphone-line', title: '多端支持', desc: '持续收录移动端适配游戏，手机电脑都能玩' },
                { icon: 'ri-customer-service-2-line', title: '贴心服务', desc: '7×24小时客服支持，遇到问题随时联系我们' },
              ].map((feature) => (
                <div key={feature.title} className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6 hover:border-primary-300/40 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary-100/70 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-3">联系我们</h2>
            <p className="text-sm md:text-base text-foreground-600 text-center mb-10">有任何问题或建议？欢迎通过以下方式联系我们</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', title: '电子邮件', value: 'support@18webgame.com' },
                { icon: 'ri-wechat-line', title: '微信公众号', value: '18网页游戏' },
                { icon: 'ri-customer-service-2-line', title: '在线客服', value: '工作日 9:00-18:00' },
              ].map((contact) => (
                <div key={contact.title} className="text-center p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-accent-100/70 flex items-center justify-center">
                    <i className={`${contact.icon} text-lg text-accent-600`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">{contact.title}</h3>
                  <p className="text-xs text-foreground-500">{contact.value}</p>
                </div>
              ))}
            </div>

            <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-8">
              <h3 className="text-base font-semibold text-foreground-900 mb-6 text-center">发送消息</h3>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-300 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-300 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-300 transition-all duration-200"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-300 transition-all duration-200 resize-none"
                    placeholder="请输入您的消息（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="visually-hidden" />
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-secondary-600 text-center">消息已成功发送，我们会尽快回复您！</p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="text-sm text-accent-600 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}